import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ApiError, type InstanceGroupConsolidated, type InstanceGroupSummary, type InstanceSummary, type PortfolioOrganisationNode, type SavedView } from "@keydials/api-client";
import { api, getAccessToken, useAuth } from "../auth/AuthContext";
import { OrgNamePrompt, useOrgNamePrompt } from "../components/OrgNamePrompt";
import { buildDialVM, type DialVM } from "../dialModel";
import {
  applyFilters,
  CHECKING_SECTION_KEY,
  DEFAULT_VIEW,
  groupInstances,
  parseViewConfig,
  serialiseViewConfig,
  type Grouping,
  type PortfolioRow,
  type SortKey,
  type ViewConfig,
} from "../viewModel";
import { latestUpdate } from "../components/DialCard";
import { DialTile, DialTileSkeletons, TileRows, useTilesPerRow } from "../components/DialTile";
import { Icon } from "../components/Icon";
import { timeAgo } from "../lib/ui";
import { GroupDialCard } from "../groups/GroupDial";
import { GroupPicker } from "../groups/GroupPicker";
import { showNewGroup, showViewTools } from "./dashboardTooling";
import { claimSoleDialLanding } from "../lib/landing";
import { useAuthoring } from "../authoring/AuthoringProvider";
import { BeingBuiltShelf } from "../authoring/BeingBuiltShelf";
import { BuildFirstDial } from "../authoring/BuildFirstDial";
import { builderCopy } from "../authoring/copy";

interface Card {
  summary: InstanceSummary;
  vm: DialVM | null;
  updated: string;
  /** The dial asks for something before it opens (KEY-434), so its detail is being withheld. Without
   * this the card would sit as a skeleton forever with nothing to say why. */
  needsSetup?: boolean;
}

const selectStyle: React.CSSProperties = {
  padding: "8px 12px",
  borderRadius: "var(--radius-sm)",
  border: "var(--border-w) solid var(--border)",
  background: "var(--surface)",
  color: "var(--text)",
  font: "inherit",
  fontSize: 13.5,
  fontWeight: 600,
  cursor: "pointer",
};

const actionStyle: React.CSSProperties = {
  ...selectStyle,
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
};

/** The grouping/sort/saved-view controls (KEY-133/134). */
function ViewBar({
  config,
  setConfig,
  views,
  activeViewId,
  dirty,
  onSelectView,
  onSaveAs,
  onUpdate,
  onSetDefault,
  onDelete,
}: {
  config: ViewConfig;
  setConfig: (c: ViewConfig) => void;
  views: SavedView[];
  activeViewId: string | null;
  dirty: boolean;
  onSelectView: (id: string) => void;
  onSaveAs: () => void;
  onUpdate: () => void;
  onSetDefault: () => void;
  onDelete: () => void;
}) {
  const activeView = views.find((v) => v.id === activeViewId) ?? null;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", marginBottom: 22 }}>
      <select
        aria-label="Saved view"
        value={activeViewId ?? ""}
        onChange={(e) => (e.target.value ? onSelectView(e.target.value) : setConfig({ ...DEFAULT_VIEW }))}
        style={selectStyle}
      >
        <option value="">Default view</option>
        {views.map((v) => (
          <option key={v.id} value={v.id}>
            {v.name}
            {v.isDefault ? " ★" : ""}
          </option>
        ))}
      </select>

      <span style={{ width: 1, height: 22, background: "var(--border)" }} />

      <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--text-muted)", fontWeight: 600 }}>
        Group
        <select aria-label="Grouping" value={config.grouping} onChange={(e) => setConfig({ ...config, grouping: e.target.value as Grouping })} style={selectStyle}>
          <option value="organisation">Organisation</option>
          <option value="definition">Definition</option>
          <option value="none">None</option>
        </select>
      </label>

      <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--text-muted)", fontWeight: 600 }}>
        Sort
        <select aria-label="Sort" value={config.sort} onChange={(e) => setConfig({ ...config, sort: e.target.value as SortKey })} style={selectStyle}>
          <option value="name">Name</option>
          <option value="recent">Most recent</option>
          <option value="completion">Least complete</option>
        </select>
      </label>

      <span style={{ flex: 1 }} />

      {activeView && dirty && (
        <button onClick={onUpdate} className="kd-focusable" style={actionStyle} title="Save changes to this view">
          <Icon name="check" size={14} /> Save
        </button>
      )}
      {activeView && !activeView.isDefault && (
        <button onClick={onSetDefault} className="kd-focusable" style={actionStyle} title="Open this view by default">
          Set default
        </button>
      )}
      {activeView && (
        <button onClick={onDelete} className="kd-focusable" style={actionStyle} title="Delete this view">
          <Icon name="trash" size={14} />
        </button>
      )}
      <button onClick={onSaveAs} className="kd-focusable" style={actionStyle} title="Save the current grouping, sort and search as a new view">
        <Icon name="plus" size={14} /> Save as view
      </button>
    </div>
  );
}

export function Dashboard() {
  const navigate = useNavigate();
  // KEY-560: the ways into the dial builder, for someone it's offered to. The one-dial hop to a sole dial is unchanged; the
  // avatar menu is how those people reach the builder.
  const authoring = useAuthoring();
  const location = useLocation();
  // KEY-743: the organisation-name check, for a buyer whose organisation we named from their email address.
  const { me } = useAuth();
  const orgNamePrompt = useOrgNamePrompt();
  // Workspace bounces here with this flag when its instance fetch 404s (a deleted/reset-away dial, or a
  // stale bookmark/link/notification) instead of blanking the screen. Show a one-off notice, then wipe the
  // history state so a refresh or back-navigation doesn't resurface it.
  const [dialGoneNotice, setDialGoneNotice] = useState(
    () => Boolean((location.state as { dialUnavailable?: boolean } | null)?.dialUnavailable),
  );
  useEffect(() => {
    if (!dialGoneNotice) {
      return;
    }
    window.history.replaceState({}, "");
    const timer = window.setTimeout(() => setDialGoneNotice(false), 6000);
    return () => window.clearTimeout(timer);
  }, [dialGoneNotice]);
  const [cards, setCards] = useState<Card[] | null>(null);
  const [orgs, setOrgs] = useState<PortfolioOrganisationNode[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [views, setViews] = useState<SavedView[]>([]);
  const [config, setConfig] = useState<ViewConfig>({ ...DEFAULT_VIEW });
  const [activeViewId, setActiveViewId] = useState<string | null>(null);
  const [groups, setGroups] = useState<InstanceGroupSummary[]>([]);
  const [groupCons, setGroupCons] = useState<Map<string, InstanceGroupConsolidated>>(new Map());
  const [picking, setPicking] = useState(false);

  // The portfolio is cross-org; clear any per-dial scope so each card's detail is fetched against its own org.
  useEffect(() => {
    api.setActiveOrg(null);
    const token = getAccessToken();
    if (!token) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const [portfolio, savedViews, myGroups] = await Promise.all([
          api.listMyInstances(token),
          api.listSavedViews(token),
          api.listMyGroups(token),
        ]);
        if (cancelled) {
          return;
        }
        // Paint the page shell (orgs, saved views, groups) as soon as the lightweight lists land,
        // before the per-dial detail fan-out below (KEY-195).
        setOrgs(portfolio.organisations);
        setViews(savedViews);
        setGroups(myGroups);
        const def = savedViews.find((v) => v.isDefault);
        if (def) {
          setConfig(parseViewConfig(def.configJson));
          setActiveViewId(def.id);
        }

        // A dial that's in a group is shown as the group ring, not as its own card — so the standalone
        // list can't be drawn until we know which dials those are. /api/me/groups now carries each group's
        // visible member ids, so we know immediately (KEY-238); before that the ONLY source was the
        // per-group consolidated rollups, and every card on the page waited on the slowest of them.
        //
        // When the API is older than this build and omits the ids, fall back to the old ordering rather
        // than painting cards we'd then have to yank out from under the user — a correct-but-later grid
        // beats a flickering one. The fast path is the one that ships; the fallback just keeps a version
        // skew from looking broken.
        const knownMembers = myGroups.every((g) => g.memberInstanceIds != null);
        const memberIdsFromSummaries = new Set(myGroups.flatMap((g) => g.memberInstanceIds ?? []));

        // Independent of the cards below: each group's consolidated rollup feeds its ring, and lands
        // whenever it lands. GroupDialCard already renders from `consolidated: null`, so the group row
        // paints with the summaries above and fills in here.
        const consolidatedSettled = Promise.allSettled(myGroups.map((g) => api.getGroupConsolidated(token, g.id)))
          .then((consResults) => {
            if (cancelled) {
              return new Set<string>();
            }
            const consByGroup = new Map<string, InstanceGroupConsolidated>();
            const memberIds = new Set<string>();
            consResults.forEach((r, i) => {
              if (r.status === "fulfilled") {
                consByGroup.set(myGroups[i].id, r.value);
                for (const m of r.value.members) memberIds.add(m.instanceId);
              }
            });
            setGroupCons(consByGroup);
            return memberIds;
          });

        const memberIds = knownMembers ? memberIdsFromSummaries : await consolidatedSettled;
        if (cancelled) {
          return;
        }
        const standalone = portfolio.instances.filter((s) => !memberIds.has(s.id));

        // Nothing to choose between: one dial (or one group) and no second thing means My Dials is a page
        // with a single card on it, so land straight on that dial's overview. Only the first arrival of the
        // session hops — see claimSoleDialLanding — so My Dials stays reachable afterwards.
        const soleDial = standalone.length === 1 && myGroups.length === 0 ? standalone[0] : null;
        const soleGroup = standalone.length === 0 && myGroups.length === 1 ? myGroups[0] : null;
        if ((soleDial || soleGroup) && claimSoleDialLanding()) {
          if (soleDial) {
            navigate(`/instances/${soleDial.id}`, { replace: true, state: { organisationId: soleDial.organisationId } });
          } else {
            navigate(`/groups/${soleGroup!.id}`, { replace: true });
          }
          return;
        }

        // The server pre-computes render detail for the dials it can safely batch (KEY-195): those cards
        // draw immediately from this one response. Anything it didn't cover — a scoped-visibility dial, a
        // mirror-face group, an unloadable version — is seeded as a skeleton and filled by its own detail
        // request, progressively, so dials still appear one by one rather than all-or-nothing.
        //
        // Group dials used to be in that second category wholesale (KEY-521), which meant a full instance
        // GET each just to draw a 104px ring. The skip below needs no change for them: it keys off whatever
        // the batch actually returned, not off what kind of dial it is.
        const preById = new Map((portfolio.cards ?? []).map((d) => [d.id, d]));
        setCards(
          standalone.map((summary) => {
            const pre = preById.get(summary.id);
            return pre
              ? { summary, vm: buildDialVM(pre), updated: timeAgo(latestUpdate(pre, summary.createdAtUtc)) }
              : { summary, vm: null, updated: timeAgo(summary.createdAtUtc) };
          }),
        );

        // Belt and braces on the fast path: the consolidated responses are the authority on membership,
        // so if one names a member the summary didn't, drop that card when it lands. Both sets come from
        // the same server logic milliseconds apart, so in practice this removes nothing — it exists so a
        // membership changing mid-load can't strand a dial in both the ring and the grid.
        if (knownMembers && myGroups.length > 0) {
          void consolidatedSettled.then((authoritative) => {
            if (cancelled || authoritative.size === 0) {
              return;
            }
            setCards((prev) => {
              if (!prev || !prev.some((c) => authoritative.has(c.summary.id))) {
                return prev; // nothing to remove — keep the identity so the grid doesn't re-render
              }
              return prev.filter((c) => !authoritative.has(c.summary.id));
            });
          });
        }

        for (const summary of standalone) {
          if (preById.has(summary.id)) {
            continue; // already rendered from the batched detail
          }
          void api.getInstance(token, summary.id, summary.organisationId).then(
            (detail) => {
              if (cancelled) {
                return;
              }
              setCards((prev) =>
                prev
                  ? prev.map((c) =>
                      c.summary.id === summary.id
                        ? { summary, vm: buildDialVM(detail), updated: timeAgo(latestUpdate(detail, summary.createdAtUtc)) }
                        : c,
                    )
                  : prev,
              );
            },
            (e) => {
              if (cancelled) {
                return;
              }
              // KEY-434: a 428 is not a failure — the dial is there and theirs, it just has not been
              // told something it insists on. Badge the card so the blank ring reads as "not set up
              // yet" rather than "still loading"; opening it lands on the capture screen.
              if (e instanceof ApiError && e.status === 428) {
                setCards((prev) =>
                  prev
                    ? prev.map((c) => (c.summary.id === summary.id ? { ...c, needsSetup: true } : c))
                    : prev,
                );
              }
              // Any other failure leaves the card in its skeleton (vm: null) state; the rest of the
              // portfolio still renders.
            },
          );
        }
      } catch (e) {
        if (cancelled) {
          return;
        }
        // A 402 — the trial ran out under an open session — is not caught here any more (KEY-637): the api
        // client tells the auth context, and App.tsx swaps to the trial-ended screen in the same tick this
        // catch runs, so the error set below is never shown.
        setError(e instanceof Error ? e.message : "Failed to load dials.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const cardById = useMemo(() => new Map((cards ?? []).map((c) => [c.summary.id, c])), [cards]);

  const rows: PortfolioRow[] = useMemo(
    () =>
      (cards ?? []).map((c) => ({
        id: c.summary.id,
        name: c.summary.name,
        organisationId: c.summary.organisationId,
        organisationName: c.summary.organisationName,
        organisationParentId: c.summary.organisationParentId,
        definitionId: c.summary.definitionId,
        definitionName: c.summary.definitionName,
        createdAtUtc: c.summary.createdAtUtc,
        completion: c.vm?.completion ?? 0,
        isTesting: c.summary.isTesting ?? false,
      })),
    [cards],
  );

  // KEY-711: with fewer than two standalone dials the view tools are hidden, so the view they'd
  // have configured must not apply either (no "DEFINITION · 1" heading, no saved search hiding the
  // only tile). `config` itself stays untouched so a saved default view isn't mutated.
  const toolsShown = showViewTools(cards?.length ?? 0);
  const effectiveConfig = useMemo<ViewConfig>(
    () => (toolsShown ? config : { ...config, grouping: "none", search: "" }),
    [toolsShown, config],
  );
  const sections = useMemo(() => groupInstances(applyFilters(rows, effectiveConfig), orgs, effectiveConfig), [rows, effectiveConfig, orgs]);

  // One tile width for the whole page: measured once on the sections container and shared by every
  // section, so the dial sections read as a single grid rather than a stack of differently-sized ones.
  const [tilesRef, tilesPerRow] = useTilesPerRow();

  const activeView = views.find((v) => v.id === activeViewId) ?? null;
  const dirty = activeView ? serialiseViewConfig(parseViewConfig(activeView.configJson)) !== serialiseViewConfig(config) : false;

  async function refreshViews(): Promise<SavedView[]> {
    const token = getAccessToken();
    if (!token) return views;
    const next = await api.listSavedViews(token);
    setViews(next);
    return next;
  }

  function selectView(id: string) {
    const view = views.find((v) => v.id === id);
    if (!view) return;
    setActiveViewId(id);
    setConfig(parseViewConfig(view.configJson));
  }

  async function saveAs() {
    const token = getAccessToken();
    const name = window.prompt("Name this view")?.trim();
    if (!token || !name) return;
    try {
      const created = await api.createSavedView(token, { name, configJson: serialiseViewConfig(config) });
      await refreshViews();
      setActiveViewId(created.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not save the view.");
    }
  }

  async function updateView() {
    const token = getAccessToken();
    if (!token || !activeView) return;
    try {
      await api.updateSavedView(token, activeView.id, { name: activeView.name, configJson: serialiseViewConfig(config), isDefault: activeView.isDefault });
      await refreshViews();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not update the view.");
    }
  }

  async function setDefaultView() {
    const token = getAccessToken();
    if (!token || !activeView) return;
    try {
      await api.setSavedViewDefault(token, activeView.id);
      await refreshViews();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not set the default view.");
    }
  }

  async function deleteView() {
    const token = getAccessToken();
    if (!token || !activeView || !window.confirm(`Delete the view “${activeView.name}”?`)) return;
    try {
      await api.deleteSavedView(token, activeView.id);
      await refreshViews();
      setActiveViewId(null);
      setConfig({ ...DEFAULT_VIEW });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not delete the view.");
    }
  }

  return (
    <div style={{ maxWidth: 1180, margin: "0 auto", padding: "36px 28px 80px" }}>
      {/* KEY-743: also asked here, not only on a dial — though a single-dial buyer is usually sent straight to
          that dial (see claimSoleDialLanding), and is asked there. */}
      {orgNamePrompt.show && me?.organisationName && (
        <OrgNamePrompt
          key={`org-name:${me.organisationId}`}
          organisationName={me.organisationName}
          onDismiss={orgNamePrompt.dismiss}
        />
      )}
      {dialGoneNotice && (
        <div
          className="kd-card"
          role="status"
          style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18, padding: "12px 16px" }}
        >
          <span style={{ flex: 1, fontSize: 14, color: "var(--text)" }}>
            That dial is no longer available — it may have been removed. Here are the dials you can still reach.
          </span>
          <button
            type="button"
            onClick={() => setDialGoneNotice(false)}
            className="kd-focusable"
            aria-label="Dismiss"
            style={{ border: "none", background: "transparent", color: "var(--text-muted)", cursor: "pointer", font: "inherit", padding: 4, lineHeight: 1, display: "inline-flex" }}
          >
            <Icon name="x" size={16} />
          </button>
        </div>
      )}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 18,
          alignItems: "flex-end",
          justifyContent: "space-between",
          marginBottom: 26,
        }}
      >
        <div>
          <h1 style={{ fontSize: 30, fontWeight: 800, margin: 0, color: "var(--text)" }}>My Dials</h1>
          <p style={{ margin: "6px 0 0", color: "var(--text-muted)", fontSize: 15 }}>Every dial you can reach, across your organisations.</p>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>
          {authoring.offered && (
            <button
              type="button"
              onClick={() => navigate("/build/new")}
              className="kd-focusable"
              style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "11px 16px", borderRadius: "var(--radius)", border: "none", background: "var(--brand-600, #4338ca)", color: "#fff", font: "inherit", fontWeight: 700, fontSize: 14, cursor: "pointer" }}
            >
              <Icon name="plus" size={16} /> {builderCopy.waysIn.buildButton}
            </button>
          )}
          {cards !== null && showNewGroup(cards.length) && (
            <button
              onClick={() => setPicking(true)}
              className="kd-focusable"
              style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "11px 16px", borderRadius: "var(--radius)", border: "none", background: "var(--brand-600, #4338ca)", color: "#fff", font: "inherit", fontWeight: 700, fontSize: 14, cursor: "pointer" }}
              title="Group dials into one view"
            >
              <Icon name="layers" size={16} /> New group
            </button>
          )}
          {toolsShown && (
          <div style={{ position: "relative", width: 260, maxWidth: "100%" }}>
          <Icon
            name="search"
            size={17}
            style={{ position: "absolute", left: 13, top: "50%", transform: "translateY(-50%)", color: "var(--text-faint)" }}
          />
          <input
            value={config.search}
            onChange={(e) => setConfig({ ...config, search: e.target.value })}
            placeholder="Search dials…"
            className="kd-focusable"
            style={{
              width: "100%",
              padding: "11px 14px 11px 38px",
              borderRadius: "var(--radius)",
              border: "var(--border-w) solid var(--border)",
              background: "var(--surface)",
              color: "var(--text)",
              font: "inherit",
              fontSize: 14,
              outline: "none",
            }}
          />
          </div>
          )}
        </div>
      </div>

      {/* KEY-628: the "Active dials / Need attention / Overall complete" cards were removed. Summed
          across every dial they mixed palettes and scales that mean different things (a ninth status
          skewed the whole score), so the totals read as precise while measuring nothing in particular.
          Each dial's own card carries its own figures. */}
      {cards !== null && toolsShown && (
        <ViewBar
          config={config}
          setConfig={setConfig}
          views={views}
          activeViewId={activeViewId}
          dirty={dirty}
          onSelectView={selectView}
          onSaveAs={saveAs}
          onUpdate={updateView}
          onSetDefault={setDefaultView}
          onDelete={deleteView}
        />
      )}

      {error && <p style={{ background: "var(--surface)", border: "var(--border-w) solid var(--border)", borderRadius: "var(--radius)", padding: "12px 16px", color: "var(--st-overdue)", fontSize: 14 }}>{error}</p>}

      {cards === null && !error && <DialTileSkeletons />}

      {cards !== null &&
        cards.length === 0 &&
        (authoring.offered ? (
          <BuildFirstDial onStart={(start) => navigate(`/build/new?start=${start}`)} />
        ) : (
          <p style={{ color: "var(--text-muted)", fontSize: 15 }}>No dials are visible to you yet.</p>
        ))}

      {authoring.offered && <BeingBuiltShelf />}

      {groups.length > 0 && (
        <section style={{ marginBottom: 26 }}>
          <h2 style={{ fontSize: 14, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.04em", margin: "0 0 12px" }}>
            My groups <span style={{ color: "var(--text-faint)", fontWeight: 600 }}>· {groups.length}</span>
          </h2>
          <div className="kd-grid">
            {groups.map((g) => (
              <GroupDialCard key={g.id} summary={g} consolidated={groupCons.get(g.id) ?? null} onOpen={() => navigate(`/groups/${g.id}`)} />
            ))}
          </div>
        </section>
      )}

      {cards !== null && cards.length > 0 && (
        <div ref={tilesRef} style={{ display: "flex", flexDirection: "column", gap: 26 }}>
          {sections.map((section) => (
            <section key={section.key}>
              {/* "Sent for checking" keeps its heading even under None grouping — without it the
                  section would silently merge into the rest of the page (KEY-516). */}
              {(effectiveConfig.grouping !== "none" || section.key === CHECKING_SECTION_KEY) && (
                <h2
                  style={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: "var(--text-muted)",
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                    margin: `0 0 12px ${section.depth * 18}px`,
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  {section.depth > 0 && <Icon name="chevRight" size={13} style={{ color: "var(--text-faint)", opacity: 0.6 }} />}
                  {section.label}
                  <span style={{ color: "var(--text-faint)", fontWeight: 600 }}>· {section.rows.length}</span>
                </h2>
              )}
              {section.rows.length > 0 && (
                <TileRows
                  items={section.rows}
                  perRow={tilesPerRow}
                  keyOf={(row) => row.id}
                  style={{ marginLeft: effectiveConfig.grouping === "organisation" ? section.depth * 18 : 0 }}
                >
                  {(row) => {
                    const card = cardById.get(row.id);
                    // No "Testing" badge here: the section heading already says these are out for checking.
                    // The red outline is still worth it (KEY-520) — the heading is only visible if you look
                    // up, and it scrolls away, while the outline travels with the tile.
                    return card ? (
                      <DialTile
                        vm={card.vm}
                        name={card.summary.name}
                        org={card.summary.organisationName}
                        definitionName={card.summary.definitionName}
                        updated={card.updated}
                        needsSetup={card.needsSetup ?? card.summary.needsFirstRunCapture ?? false}
                        group={card.summary.isGroup ?? false}
                        testing={card.summary.isTesting ?? false}
                        demo={card.summary.isDemo ?? false}
                        onOpen={() => navigate(`/instances/${row.id}`, { state: { organisationId: row.organisationId } })}
                      />
                    ) : null;
                  }}
                </TileRows>
              )}
            </section>
          ))}
          {sections.length === 0 && (
            <div style={{ color: "var(--text-muted)", padding: 40, textAlign: "center" }}>No dials match your filters.</div>
          )}
        </div>
      )}

      {picking && (
        <GroupPicker
          instances={(cards ?? []).map((c) => c.summary)}
          onClose={() => setPicking(false)}
          onCreated={(detail) => {
            setPicking(false);
            navigate(`/groups/${detail.id}`);
          }}
        />
      )}
    </div>
  );
}
